import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const fmt = (iso) => (iso ? iso.slice(0, 10) : "—");

export const RMQuestionBoard = () => {
  const [data, setData] = useState(null);

  const load = () => api.get("/admin/rm-training/questions")
    .then((r) => setData(r.data)).catch(() => setData({ open: [], history: [] }));
  useEffect(() => { load(); }, []);

  const discuss = async (qid) => {
    try {
      await api.post(`/admin/rm-training/questions/${qid}/discussed`);
      toast.success("Archived to discussion history");
      load();
    } catch (_) { toast.error("Couldn't archive — try again"); }
  };

  if (!data) return <p className="text-sm text-neutral-500">Loading…</p>;

  const byMonday = data.open.reduce((acc, q) => {
    (acc[q.monday_date] = acc[q.monday_date] || []).push(q);
    return acc;
  }, {});

  return (
    <div data-testid="admin-rm-question-board">
      <p className="text-sm text-neutral-400">
        Questions RMs submitted for the Monday check-ins, grouped by session date and sorted by module.
        Mark each as discussed after the session to archive it.
      </p>

      {Object.keys(byMonday).length === 0 ? (
        <p className="mt-5 rounded-2xl border border-neutral-800 bg-neutral-950 py-8 text-center text-sm text-neutral-500" data-testid="admin-qboard-empty">
          No open questions — the board is clear.
        </p>
      ) : Object.entries(byMonday).map(([monday, qs]) => (
        <div key={monday} className="mt-5" data-testid={`admin-qboard-monday-${monday}`}>
          <p className="text-sm font-bold text-[#2dd4bf]">Monday {monday} <span className="text-neutral-500">({qs.length})</span></p>
          <div className="mt-2 space-y-2">
            {qs.map((q) => (
              <div key={q.question_id}
                className="flex items-start justify-between gap-3 rounded-xl border border-neutral-800 bg-neutral-950 px-4 py-3"
                data-testid={`admin-qboard-q-${q.question_id}`}>
                <div>
                  <p className="text-sm text-neutral-100">{q.question}</p>
                  <p className="mt-1 text-[11px] text-neutral-500">
                    <span className="font-semibold text-neutral-300">{q.rm_name}</span> · M{q.module_id}: {q.module_title} · submitted {fmt(q.created_at)}
                  </p>
                </div>
                <button onClick={() => discuss(q.question_id)}
                  className="flex shrink-0 items-center gap-1 rounded-md border border-emerald-500/40 px-2.5 py-1.5 text-[10px] font-bold text-emerald-400 hover:bg-emerald-500/10"
                  data-testid={`admin-qboard-discuss-${q.question_id}`}>
                  <CheckCircle2 size={12} /> Mark as Discussed
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="mt-8">
        <p className="text-sm font-bold text-neutral-300">Discussion History ({data.history.length})</p>
        <div className="mt-2 space-y-2" data-testid="admin-qboard-history">
          {data.history.length === 0 ? (
            <p className="text-xs text-neutral-500">Nothing archived yet.</p>
          ) : data.history.map((q) => (
            <div key={q.question_id} className="rounded-xl border border-neutral-800/60 bg-neutral-900/40 px-4 py-2.5">
              <p className="text-sm text-neutral-400">{q.question}</p>
              <p className="mt-0.5 text-[11px] text-neutral-600">
                {q.rm_name} · M{q.module_id} · discussed {fmt(q.discussed_at)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
